import {
  all,
  call,
  fork,
  put,
  delay,
  spawn,
  cancel,
  cancelled,
  take,
  takeEvery,
} from 'redux-saga/effects';
import { aiProductsApi } from '@api';
import {
  LOAD_AI_PRODUCT_LIST,
  CREATE_AI_PRODUCT,
  UPDATE_AI_PRODUCT,
  DELETE_AI_PRODUCT,
  LOAD_AI_PRODUCT,
  loadAiProductList,
  renderAiProductList,
  renderAiProduct,
  PROCESS_AI_PRODUCT,
} from './actions';
import { actions as appActions } from '@store/app/index';

const STOP_SYNC_AI_PRODUCT = 'STOP_SYNC_AI_PRODUCT';

function* fetchOrSyncAiProduct({ id }) {
  while (true) {
    const data = yield call(aiProductsApi.getAiProduct, id);
    yield put(renderAiProduct(data));

    const images = data?.images ?? [];
    const texts = data?.texts ?? [];
    const lifestyles = images.flatMap((image) => image.lifestyle);

    const active = [
      ...(images.map((image) => [
        'GROUNDING',
        'REMOVING_BACKGROUND',
        'SHADOWING',
        'BACKGROUND_GENERATION',
        'VIDEO_GENERATION',
        'TEXT_GENERATION',
      ].includes(image.state))),
      ...(lifestyles.map((lifestyle) => [
        'BACKGROUND_GENERATION',
      ].includes(lifestyle.state))),
      ...(texts.map((text) => [
        'TEXT_GENERATION',
      ].includes(text.state))),
    ];

    if (active.every((v) => v === false)) {
      break;
    }

    yield delay(5000);
  }
}

export function* fetchAiProductList() {
  try {
    yield put({ type: STOP_SYNC_AI_PRODUCT });
    const data = yield call(aiProductsApi.getAiProducts);
    yield put(renderAiProductList(data));
  } catch (error) {
    console.error(error);
  }
}

export function* fetchAiProduct({ id }) {
  try {
    yield put({ type: STOP_SYNC_AI_PRODUCT });
    const task = yield fork(fetchOrSyncAiProduct, { id });
    yield take(STOP_SYNC_AI_PRODUCT);
    yield cancel(task);
  } catch (error) {
    console.error(error);
  }
}

export function* fetchPostAiProduct({ product, setError, onComplete }) {
  try {
    yield put({ type: STOP_SYNC_AI_PRODUCT });
    const data = yield call(aiProductsApi.createAiProduct, product);
    yield put(renderAiProduct({
      ...data,
    }));
    if (onComplete) {
      onComplete(data);
    }
    yield put(loadAiProductList());
  } catch (error) {
    if (error.response?.data) {
      const apiError = error.response?.data;
      const titles = apiError.message.map((message) => {
        if (message.reason === 'unique') {
          setError(message.property, {});
          return `Property '${message.property}' should be unique`;
        }
      });
      yield put(appActions.addMessage({ message: titles.join('\n'), type: 'error' }));
      return;
    }
    console.error(error);
    yield put(appActions.addMessage({ message: 'Unexpected error during creating the Product', type: 'error' }));
  }
}

export function* fetchPutAiProduct({ product, setError, onComplete }) {
  try {
    yield put({ type: STOP_SYNC_AI_PRODUCT });
    const data = yield call(aiProductsApi.updateAiProduct, product);
    yield put(renderAiProduct({
      ...data,
    }));
    if (onComplete) {
      onComplete(data);
    }
    yield put(loadAiProductList());
  } catch (error) {
    if (error.response?.data) {
      const apiError = error.response?.data;
      const titles = apiError.message.map((message) => {
        if (message.reason === 'unique') {
          setError(message.property, {});
          return `Property '${message.property}' should be unique`;
        }
      });
      yield put(appActions.addMessage({ message: titles.join('\n'), type: 'error' }));
      return;
    }
    console.error(error);
    yield put(appActions.addMessage({ message: 'Unexpected error during creating the Product', type: 'error' }));
  }
}

export function* fetchDeleteAiProduct({ id }) {
  try {
    yield put({ type: STOP_SYNC_AI_PRODUCT });
    yield call(aiProductsApi.deleteAiProduct, id);
    yield put(loadAiProductList());
  } catch (error) {
    console.error(error);
  }
}

export function* fetchPostProcessAiProduct({ id, config }) {
  try {
    yield put({ type: STOP_SYNC_AI_PRODUCT });
    yield spawn(aiProductsApi.processAiProduct, id, config);
    yield delay(2000);
    yield fork(fetchAiProduct, { id });
  } catch (error) {
    console.error(error);
  }
}

export default function* aiProductSaga() {
  yield all([
    takeEvery(LOAD_AI_PRODUCT_LIST, fetchAiProductList),
    takeEvery(CREATE_AI_PRODUCT, fetchPostAiProduct),
    takeEvery(UPDATE_AI_PRODUCT, fetchPutAiProduct),
    takeEvery(DELETE_AI_PRODUCT, fetchDeleteAiProduct),
    takeEvery(LOAD_AI_PRODUCT, fetchAiProduct),
    takeEvery(PROCESS_AI_PRODUCT, fetchPostProcessAiProduct),
  ]);
}
